import React from 'react';
import {FormattedMessage} from 'react-intl';
import {RadioButton} from '@/web-components';
import {contactTakenBy, groupKey, isGroupResolved} from './resolution_state';

const contactLabel = contact => (contact.org ? `${contact.name} (${contact.org})` : contact.name);

/**
 * A row that cannot be chosen, with the reason beside it.
 *
 * One case only now: a Xero contact already taken by another client in this session. Clients
 * already linked to another Xero customer are filtered out by the backend rather than shown
 * here, per AC1 and AC2's "unlinked" wording.
 */
const DisabledOption = ({label, reason}) => (
	<div className="xero-match-option xero-match-option--disabled">
		<RadioButton checked={false} disabled={true} />
		<span className="xero-match-option-label">{label}</span>
		<span className="xero-match-option-reason">{reason}</span>
	</div>
);

const Option = ({label, checked, onClick, cy}) => (
	<div className={`xero-match-option${checked ? ' xero-match-option--checked' : ''}`}>
		<RadioButton checked={checked} onClick={onClick} cy={cy} />
		<span className="xero-match-option-label">{label}</span>
	</div>
);

const GroupHeader = ({group, resolved}) => (
	<div className="xero-match-group-header">
		<div className="xero-match-group-title">
			{group.kind === 'MANY_CLIENTS' ? (
				<FormattedMessage
					id="integrations.xero.match_review.many_clients.title"
					values={{count: group.clients.length, contact: contactLabel(group.xeroContacts[0])}}
				/>
			) : group.kind === 'MANY_CONTACTS' ? (
				<FormattedMessage
					id="integrations.xero.match_review.many_contacts.title"
					values={{count: group.xeroContacts.length, client: group.clients[0].name}}
				/>
			) : (
				<FormattedMessage
					id="integrations.xero.match_review.many_to_many.title"
					values={{clientCount: group.clients.length, contactCount: group.xeroContacts.length}}
				/>
			)}
		</div>
		<div className={`xero-match-badge${resolved ? ' xero-match-badge--resolved' : ''}`}>
			<FormattedMessage
				id={resolved ? 'integrations.xero.match_review.resolved' : 'integrations.xero.match_review.needs_review'}
			/>
		</div>
		<div className="xero-match-group-email">{group.email}</div>
	</div>
);

/**
 * One ambiguity, in whichever of the three shapes the backend produced (T4-135).
 *
 * The three exist because the match relation partitions by email, so a group is either one
 * customer against several clients, several customers against one client, or both at once.
 */
const MatchGroupCard = ({group, selection, onSelect}) => {
	const key = groupKey(group);
	const chosen = selection[key] || {};
	const resolved = isGroupResolved(group, selection);

	if (group.kind === 'MANY_CLIENTS') {
		return (
			<div className="xero-match-group" data-cy="xero-match-group">
				<GroupHeader group={group} resolved={resolved} />
				<div className="xero-match-group-prompt">
					<FormattedMessage id="integrations.xero.match_review.many_clients.prompt" />
				</div>
				{group.clients.map(client => (
					<Option
						key={client.clientId}
						label={client.name}
						checked={chosen.clientId === client.clientId}
						onClick={() => onSelect(key, {clientId: client.clientId})}
						cy={`xero-match-client-${client.clientId}`}
					/>
				))}
			</div>
		);
	}

	if (group.kind === 'MANY_CONTACTS') {
		const client = group.clients[0];

		return (
			<div className="xero-match-group" data-cy="xero-match-group">
				<GroupHeader group={group} resolved={resolved} />
				<div className="xero-match-group-prompt">
					<FormattedMessage id="integrations.xero.match_review.many_contacts.prompt" values={{client: client.name}} />
				</div>
				{group.xeroContacts.map(contact => (
					<Option
						key={contact.xeroContactId}
						label={contactLabel(contact)}
						checked={chosen.xeroContactId === contact.xeroContactId}
						onClick={() => onSelect(key, {xeroContactId: contact.xeroContactId})}
						cy={`xero-match-contact-${contact.xeroContactId}`}
					/>
				))}
			</div>
		);
	}

	const byClientId = chosen.byClientId || {};

	const choose = (clientId, xeroContactId) => onSelect(key, {byClientId: {...byClientId, [clientId]: xeroContactId}});

	return (
		<div className="xero-match-group" data-cy="xero-match-group">
			<GroupHeader group={group} resolved={resolved} />
			<div className="xero-match-group-prompt">
				<FormattedMessage id="integrations.xero.match_review.many_to_many.prompt" />
			</div>
			{group.clients.map(client => (
				<div className="xero-match-client-block" key={client.clientId}>
					<div className="xero-match-client-name">{client.name}</div>
					{group.xeroContacts.map(contact => {
						const takenBy = contactTakenBy(group, selection, contact.xeroContactId, client.clientId);

						return takenBy ? (
							<DisabledOption
								key={contact.xeroContactId}
								label={contactLabel(contact)}
								reason={
									<FormattedMessage id="integrations.xero.match_review.taken_by" values={{client: takenBy}} />
								}
							/>
						) : (
							<Option
								key={contact.xeroContactId}
								label={contactLabel(contact)}
								checked={byClientId[client.clientId] === contact.xeroContactId}
								onClick={() => choose(client.clientId, contact.xeroContactId)}
								cy={`xero-match-pair-${client.clientId}-${contact.xeroContactId}`}
							/>
						);
					})}
					<Option
						label={<FormattedMessage id="integrations.xero.match_review.do_not_link" />}
						checked={byClientId[client.clientId] === null}
						onClick={() => choose(client.clientId, null)}
						cy={`xero-match-skip-${client.clientId}`}
					/>
				</div>
			))}
		</div>
	);
};

export default MatchGroupCard;
